import { FC, ReactNode, useCallback } from "react";
import { Box, FormControl, InputLabel, Select, Stack } from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { HierarchyDashboardSelectionWrapper } from "@/components/pages/common/hierarchy-dashboard-selection/HierarchyDashboardSelectionWrapper";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { homeDashboardIdSelector } from "@/store/dashboards/selectors/home/homeDashboardIdSelector";
import { FilterTriggerComponentProps } from "@/components/common/select/dropdown-select/utils/types/filterTriggerComponentProps";
import { updateDashboardHierarchyDashboardThunk } from "@/store/dashboards/thunks/common/updateDashboardHierarchyDashboardThunk";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";

export const HomeDashboardSelect: FC = () => {
  const dispatch = useAppDispatch();
  const dashboardId = useAppSelector(homeDashboardIdSelector);

  const changeNavigationHandler = useCallback(
    (dbId: string) => {
      if (dashboardId === dbId) {
        return;
      }

      dispatch(
        updateDashboardHierarchyDashboardThunk({
          dashboardId: dbId,
          body: { is_home_page: true },
          updatedMessage: "Homepage successfully changed.",
        }),
      );
    },
    [dispatch, dashboardId],
  );

  return (
    <Stack width={270}>
      <HierarchyDashboardSelectionWrapper
        selectedItemId={dashboardId ?? ""}
        onChange={changeNavigationHandler}
        CustomTriggerComponent={SelectTrigger}
      />
    </Stack>
  );
};

const SelectTrigger: FC<FilterTriggerComponentProps> = ({
  onClick,
  open,
  label,
}) => {
  const renderValue = useCallback((value: ReactNode) => {
    if (value) {
      return (
        <Stack direction="row" alignItems="center">
          <GridViewOutlinedIcon fontSize="small" sx={{ pr: 1 }} />
          <TypographyWithTooltip title={value} />
        </Stack>
      );
    }
  }, []);

  return (
    <Box py={1}>
      <FormControl size="xsmall" fullWidth>
        <InputLabel>Select Dashboard</InputLabel>
        <Select
          onClick={onClick}
          size="small"
          open={false}
          label="Select Dashboard"
          value={label}
          IconComponent={open ? ArrowDropUpIcon : ArrowDropDownIcon}
          renderValue={renderValue}
        />
      </FormControl>
    </Box>
  );
};
